import React, { useEffect, useState } from "react";
import { Row, Col, Statistic, Typography, List, Flex, Empty, Spin, Tooltip, Space, Tabs, Skeleton, Button } from "antd";
import {
    UserOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    MinusCircleOutlined,
    ClockCircleOutlined,
    MailOutlined,
} from "@ant-design/icons";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import CollapsibleInsightCard from "../CollapsibleInsightCard";
import BottleneckMiniChart, { BottleneckItem } from "../BottleneckMiniChart";
import { JobDescriptionEntity } from "../../../../../../../Entities/JobDescription";
import { CHART_COLORS } from "../../../../StatsAndAnalytics/utils/chartColors";
import jobApplicationService from "../../../../../../../Service/job.application.service";
import "./index.scss";

const { Text } = Typography;

// Requirement stat interface (matches backend response)
interface RequirementStat {
    requirementId: number;
    description: string;
    topic: string;
    isMustMatch: boolean;
    stats: {
        matched: number;
        unmatched: number;
        partial: number;
        total: number;
        matchRate: number;
    };
}

interface RequirementsInsightData {
    summary: {
        totalRequirements: number;
        mustMatchCount: number;
        optionalCount: number;
        // Candidate-focused metrics
        totalApplications: number;
        totalEvaluated: number;
        pendingCount: number;
        qualifiedCount: number;
        failedCount: number;
    };
    requirementStats: RequirementStat[];
}

// Profile match status filter type
export type ProfileMatchStatusFilter = "all" | "qualified" | "unqualified" | "pending";

interface RequirementsInsightCardProps {
    jobPost: JobDescriptionEntity;
    themeColor?: string;
    onRequirementClick?: (requirementId: number, matchStatus: "matched" | "unmatched" | "partial") => void;
    /** Called when a stat card is clicked - navigates to applications with profile match filter */
    onProfileMatchStatusClick?: (status: ProfileMatchStatusFilter) => void;
    /** Controlled mode: external expanded state */
    expanded?: boolean;
    /** Controlled mode: callback when expand/collapse is toggled */
    onExpandChange?: (expanded: boolean) => void;
}

// Helper function to format time ago
const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);

    if (diffSecs < 60) {
        return "just now";
    } else if (diffMins < 60) {
        return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
    } else if (diffHours < 24) {
        const mins = diffMins % 60;
        return mins > 0 
            ? `${diffHours}h ${mins}m ago`
            : `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    }
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Skeleton loader for stat cards
const StatCardSkeleton: React.FC = () => (
    <div className="stat-box">
        <Skeleton.Input active size="small" style={{ width: 60, marginBottom: 8 }} />
        <Skeleton.Input active size="large" style={{ width: 40 }} />
    </div>
);

// Skeleton loader for chart area
const ChartSkeleton: React.FC = () => (
    <div className="chart-skeleton">
        <Skeleton.Input active size="small" style={{ width: 200, marginBottom: 12 }} />
        <Skeleton active paragraph={{ rows: 3, width: ['100%', '80%', '60%'] }} title={false} />
    </div>
);

// Skeleton loader for requirement item
const RequirementItemSkeleton: React.FC = () => (
    <List.Item className="requirement-item">
        <Flex vertical style={{ width: "100%" }} gap={8}>
            <Skeleton.Input active size="small" style={{ width: 80 }} />
            <Skeleton active paragraph={{ rows: 1, width: ['90%'] }} title={false} />
            <Flex gap={16}>
                <Skeleton.Button active size="small" style={{ width: 80 }} />
                <Skeleton.Button active size="small" style={{ width: 80 }} />
                <Skeleton.Button active size="small" style={{ width: 80 }} />
            </Flex>
        </Flex>
    </List.Item>
);

const RequirementsInsightCard: React.FC<RequirementsInsightCardProps> = ({
    jobPost,
    themeColor,
    onRequirementClick,
    onProfileMatchStatusClick,
    expanded,
    onExpandChange,
}) => {
    const [data, setData] = useState<RequirementsInsightData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastSynced, setLastSynced] = useState<Date | null>(null);
    const [, setForceUpdate] = useState(0); // For re-rendering time ago

    // Fetch requirements insights data
    const fetchData = async (isRefresh = false) => {
        if (!jobPost?._id) return;

        setLoading(true);
        setError(null);
        try {
            const response = await jobApplicationService.getRequirementsInsights(jobPost._id);
            if (!isRefresh) {
                console.log("[RequirementsInsightCard] API Response:", response);
            }
            setData(response as RequirementsInsightData);
            setLastSynced(new Date());
        } catch (err) {
            console.error("Failed to fetch requirements insights:", err);
            setError("Failed to load insights");
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchData();
    }, [jobPost?._id]);

    // Update "time ago" display every minute
    useEffect(() => {
        const interval = setInterval(() => {
            if (lastSynced) {
                setForceUpdate((prev) => prev + 1);
            }
        }, 60000); // Update every minute

        return () => clearInterval(interval);
    }, [lastSynced]);

    // Handle refresh
    const handleRefresh = () => {
        fetchData(true);
    };

    const hasRequirements = jobPost?.requirements && jobPost.requirements.length > 0;

    // Generate subtitle with last synced time
    const subtitle = lastSynced ? `Last synced: ${getTimeAgo(lastSynced)}` : undefined;

    if (!hasRequirements) {
        return (
            <CollapsibleInsightCard
                title="Profile Screening"
                subtitle={subtitle}
                icon={<UserOutlined />}
                onRefresh={handleRefresh}
                isRefreshing={loading}
                themeColor={themeColor}
                expanded={expanded}
                onExpandChange={onExpandChange}
            >
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No requirements added"
                />
            </CollapsibleInsightCard>
        );
    }

    // Check if we have candidate data
    const hasStatsData = data?.summary.totalEvaluated && data.summary.totalEvaluated > 0;
    const totalEvaluated = data?.summary.totalEvaluated || 0;

    // Get bottlenecks - must-match requirements sorted by FAILURE COUNT (highest first = biggest bottleneck)
    const bottlenecks = data?.requirementStats
        ? [...data.requirementStats]
            .filter((s) => s.isMustMatch && s.stats.unmatched > 0)
            .sort((a, b) => b.stats.unmatched - a.stats.unmatched)
            .slice(0, 3)
        : [];

    // Convert bottlenecks to chart items - showing failures out of TOTAL EVALUATED
    const bottleneckItems: BottleneckItem[] = hasStatsData
        ? bottlenecks.map((b) => {
            // Calculate failure rate against total evaluated candidates
            const failureRate = totalEvaluated > 0
                ? Math.round((b.stats.unmatched / totalEvaluated) * 100)
                : 0;
            return {
                id: b.requirementId,
                label: b.description.length > 35 ? b.description.substring(0, 35) + "..." : b.description,
                rate: failureRate,
                count: {
                    success: b.stats.unmatched, // Number of candidates who failed this requirement
                    total: totalEvaluated, // Total evaluated candidates (not per-requirement)
                },
                isCritical: failureRate >= 50,
            };
        })
        : [];

    const handleBottleneckClick = (item: BottleneckItem) => {
        onRequirementClick?.(item.id as number, "unmatched");
    };

    const handleStatClick = (status: ProfileMatchStatusFilter) => {
        onProfileMatchStatusClick?.(status);
    };

    // Handle clicking on requirement stat (matched/failed/partial)
    const handleRequirementStatClick = (
        requirementId: number,
        matchStatus: "matched" | "unmatched" | "partial"
    ) => {
        onRequirementClick?.(requirementId, matchStatus);
    };

    // Render a single requirement item
    const renderRequirementItem = (item: RequirementStat) => (
        <List.Item className="requirement-item">
            <Flex vertical style={{ width: "100%" }} gap={8}>
                {/* Requirement description */}
                <div className="requirement-info">
                    {item.topic && (
                        <Tooltip title={`Category: ${item.topic}`} placement="top">
                            <Text type="secondary" className="topic" style={{ cursor: "help" }}>
                                {item.topic}
                            </Text>
                        </Tooltip>
                    )}
                    <Tooltip 
                        title={item.isMustMatch 
                            ? "Must-have requirement - Candidates must meet this to qualify" 
                            : "Nice-to-have requirement - Improves candidate ranking but not mandatory"
                        }
                        placement="top"
                    >
                        <Text className="description" style={{ fontWeight: 500, cursor: "help" }}>
                            {item.description}
                        </Text>
                    </Tooltip>
                </div>

                {/* Candidate stats breakdown - Clickable */}
                {item.stats.total > 0 ? (
                    <Space size="middle" wrap className="stats-breakdown">
                        <Tooltip 
                            title={
                                <div>
                                    <div>{item.stats.matched} candidate{item.stats.matched !== 1 ? 's' : ''} fully meet this requirement based on their resume</div>
                                    <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>Click to view candidates</div>
                                </div>
                            }
                        >
                            <Text
                                className="stat-link"
                                onClick={() => handleRequirementStatClick(item.requirementId, "matched")}
                            >
                                <CheckCircleOutlined style={{ color: CHART_COLORS.success, marginRight: 4 }} />
                                {item.stats.matched} matched
                            </Text>
                        </Tooltip>
                        <Tooltip 
                            title={
                                <div>
                                    <div>{item.stats.unmatched} candidate{item.stats.unmatched !== 1 ? 's' : ''} do not meet this requirement{item.isMustMatch ? " and are marked unqualified" : ""}</div>
                                    <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>Click to view candidates</div>
                                </div>
                            }
                        >
                            <Text
                                className="stat-link"
                                onClick={() => handleRequirementStatClick(item.requirementId, "unmatched")}
                            >
                                <CloseCircleOutlined style={{ color: CHART_COLORS.error, marginRight: 4 }} />
                                {item.stats.unmatched} failed
                            </Text>
                        </Tooltip>
                        <Tooltip 
                            title={
                                <div>
                                    <div>{item.stats.partial} candidate{item.stats.partial !== 1 ? 's' : ''} partially meet this requirement - they have some relevant experience but may not fully match</div>
                                    <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>Click to view candidates</div>
                                </div>
                            }
                        >
                            <Text
                                className="stat-link"
                                onClick={() => handleRequirementStatClick(item.requirementId, "partial")}
                            >
                                <MinusCircleOutlined style={{ color: CHART_COLORS.warning, marginRight: 4 }} />
                                {item.stats.partial} partial
                            </Text>
                        </Tooltip>
                    </Space>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No candidate data yet" />
                )}
            </Flex>
        </List.Item>
    );

    // Determine if we're in initial load or refresh state
    const isInitialLoad = loading && !data;
    const isRefreshing = loading && !!data;

    // Calculate pass rate for semi-circle chart
    const qualifiedCount = data?.summary.qualifiedCount || 0;
    const failedCount = data?.summary.failedCount || 0;
    const pendingCount = data?.summary.pendingCount || 0;
    const passRate = totalEvaluated > 0 ? Math.round((qualifiedCount / totalEvaluated) * 100) : 0;

    // Data for semi-circle pie chart
    const chartData = [
        { name: "Qualified", value: qualifiedCount, color: CHART_COLORS.success },
        { name: "Unqualified", value: failedCount, color: CHART_COLORS.error },
    ];

    // Summary content (collapsed view)
    const summaryContent = (
        <div className="requirements-summary">
            {isInitialLoad ? (
                // Initial load - show centered spinner
                <Flex justify="center" align="center" style={{ minHeight: 100 }}>
                    <Spin size="small" />
                </Flex>
            ) : error ? (
                <Text type="danger">{error}</Text>
            ) : (
                <>
                    {/* Semi-circle Chart + Stats */}
                    {isRefreshing ? (
                        <Flex justify="center" align="center" style={{ minHeight: 140 }}>
                            <Skeleton.Avatar active size={120} shape="circle" />
                        </Flex>
                    ) : hasStatsData ? (
                        <Flex align="center" justify="center" gap={32} className="pass-rate-section">
                            {/* Semi-circle Pie Chart */}
                            <Tooltip 
                                title={
                                    <div>
                                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Profile Match Distribution</div>
                                        <div style={{ color: CHART_COLORS.success }}>● Qualified: {qualifiedCount} candidates</div>
                                        <div style={{ color: CHART_COLORS.error }}>● Unqualified: {failedCount} candidates</div>
                                    </div>
                                }
                                placement="top"
                            >
                                <div className="semi-circle-chart-container">
                                    <ResponsiveContainer width={160} height={90}>
                                        <PieChart>
                                            <Pie
                                                data={chartData}
                                                cx="50%"
                                                cy="100%"
                                                startAngle={180}
                                                endAngle={0}
                                                innerRadius={50}
                                                outerRadius={60}
                                                paddingAngle={2}
                                                dataKey="value"
                                                strokeWidth={0}
                                            >
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <Tooltip 
                                        title={`${passRate}% of evaluated candidates meet your job requirements`}
                                        placement="bottom"
                                    >
                                        <div className="chart-center-value">
                                            <Text strong style={{ fontSize: 24, color: CHART_COLORS.success, cursor: "help" }}>
                                                {passRate}%
                                            </Text>
                                        </div>
                                    </Tooltip>
                                </div>
                            </Tooltip>

                            {/* Pass Rate Stats */}
                            <Flex vertical gap={8} className="pass-rate-stats" align="flex-start" justify="flex-start">
                                <Tooltip 
                                    title={`${qualifiedCount} candidate${qualifiedCount !== 1 ? 's' : ''} matched all must-have requirements and passed the profile screening`}
                                    placement="right"
                                >
                                    <Text style={{ fontSize: 14, fontWeight: 400, cursor: "help" }}>
                                        <CheckCircleOutlined style={{ color: CHART_COLORS.success, marginRight: 8 }} />
                                        {qualifiedCount} of {totalEvaluated} cleared
                                    </Text>
                                </Tooltip>
                                {failedCount > 0 && (
                                    <Tooltip 
                                        title={
                                            <div>
                                                <div>{failedCount} candidate{failedCount !== 1 ? 's' : ''} did not meet one or more must-have requirements</div>
                                                <div style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>Click to view details</div>
                                            </div>
                                        }
                                        placement="right"
                                    >
                                        <Text 
                                            className="stat-link" 
                                            style={{ fontSize: 14, fontWeight: 400, padding: 0 }}
                                            onClick={() => handleStatClick("unqualified")}
                                        >
                                            <CloseCircleOutlined style={{ color: CHART_COLORS.error, marginRight: 8 }} />
                                            {failedCount} did not qualify
                                        </Text>
                                    </Tooltip>
                                )}
                                
                                <Tooltip 
                                    title="AI-powered analysis of candidate resumes against your job requirements to identify the best matches"
                                    placement="right"
                                >
                                    <Text type="secondary" style={{ fontSize: 14, cursor: "help" }}>
                                        Profile Screening 
                                    </Text>
                                </Tooltip>
                            </Flex>
                        </Flex>
                    ) : (
                        <Empty 
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Profile screening data will appear once candidates upload resumes" 
                            style={{ margin: "16px 0" }}
                        />
                    )}

                    {/* CV Missing CTA */}
                    {pendingCount > 0 && (
                        <div className="cv-missing-cta">
                            <Flex align="center" justify="space-between" wrap="wrap" gap={12}>
                                <Flex align="center" gap={8}>
                                    <ClockCircleOutlined style={{ color: CHART_COLORS.warning, fontSize: 16 }} />
                                    <Text type="secondary">
                                        <strong style={{ color: CHART_COLORS.warning }}>{pendingCount}</strong> candidate{pendingCount > 1 ? 's' : ''} pending resume upload
                                    </Text>
                                </Flex>
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => handleStatClick("pending")}
                                >
                                    View
                                </Button>
                            </Flex>
                        </div>
                    )}

                    {/* 
                    // COMMENTED OUT: Stats Row and Bottleneck Chart - Already available in Applications tab
                    // Stats Row - Candidate Metrics - Clickable
                    <Row gutter={[12, 12]} className="stats-row">
                        <Col xs={12} sm={6}>
                            {isRefreshing ? (
                                <StatCardSkeleton />
                            ) : (
                                <Tooltip title="View all evaluated candidates">
                                    <div
                                        className="stat-box clickable"
                                        onClick={() => handleStatClick("all")}
                                    >
                                        <Statistic
                                            title="Evaluated"
                                            value={data?.summary.totalEvaluated || 0}
                                            valueStyle={{ fontSize: 20, fontWeight: 600 }}
                                        />
                                    </div>
                                </Tooltip>
                            )}
                        </Col>
                        <Col xs={12} sm={6}>
                            {isRefreshing ? (
                                <StatCardSkeleton />
                            ) : (
                                <Tooltip title="View qualified candidates">
                                    <div
                                        className="stat-box clickable"
                                        onClick={() => handleStatClick("qualified")}
                                    >
                                        <Statistic
                                            title="Qualified"
                                            value={data?.summary.qualifiedCount || 0}
                                            valueStyle={{ fontSize: 20, fontWeight: 600, color: CHART_COLORS.success }}
                                        />
                                    </div>
                                </Tooltip>
                            )}
                        </Col>
                        <Col xs={12} sm={6}>
                            {isRefreshing ? (
                                <StatCardSkeleton />
                            ) : (
                                <Tooltip title="View unqualified candidates">
                                    <div
                                        className="stat-box clickable"
                                        onClick={() => handleStatClick("unqualified")}
                                    >
                                        <Statistic
                                            title="Unqualified"
                                            value={data?.summary.failedCount || 0}
                                            valueStyle={{ fontSize: 20, fontWeight: 600, color: CHART_COLORS.error }}
                                        />
                                    </div>
                                </Tooltip>
                            )}
                        </Col>
                        <Col xs={12} sm={6}>
                            {isRefreshing ? (
                                <StatCardSkeleton />
                            ) : (
                                <Tooltip title="View pending candidates (no resume uploaded)">
                                    <div
                                        className="stat-box clickable"
                                        onClick={() => handleStatClick("pending")}
                                    >
                                        <Statistic
                                            title="CV Missing"
                                            value={data?.summary.pendingCount || 0}
                                            valueStyle={{ fontSize: 20, fontWeight: 600, color: CHART_COLORS.warning }}
                                        />
                                    </div>
                                </Tooltip>
                            )}
                        </Col>
                    </Row>

                    // Bottlenecks - Requirements causing most failures
                    {isRefreshing ? (
                        <div className="bottlenecks-section">
                            <ChartSkeleton />
                        </div>
                    ) : hasStatsData && bottleneckItems.length > 0 ? (
                        <div className="bottlenecks-section">
                            <BottleneckMiniChart
                                title="Requirements filtering most candidates"
                                items={bottleneckItems}
                                onItemClick={handleBottleneckClick}
                                themeColor={themeColor}
                                mode="failure"
                                rateLabel="failure rate"
                                countLabel="failed"
                                totalEvaluated={totalEvaluated}
                            />
                        </div>
                    ) : !hasStatsData ? (
                        <Empty description="Analytics data will appear once candidates complete profile matching" />
                    ) : null}
                    */}
                </>
            )}
        </div>
    );

    // Get filtered requirements by type
    const mustMatchRequirements = data?.requirementStats.filter((r) => r.isMustMatch) || [];
    const otherRequirements = data?.requirementStats.filter((r) => !r.isMustMatch) || [];

    // Tab items for expanded content
    const tabItems = [
        {
            key: "must-match",
            label: (
                <Text style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }} type="danger">Must Match Requirements
                    ({mustMatchRequirements.length})
                </Text>
            ),
            children: (
                <div className="requirements-tab-content">
                    {isRefreshing ? (
                        // Show skeleton loaders during refresh
                        <>
                            <RequirementItemSkeleton />
                            <RequirementItemSkeleton />
                            <RequirementItemSkeleton />
                        </>
                    ) : mustMatchRequirements.length > 0 ? (
                        <List
                            size="small"
                            dataSource={mustMatchRequirements}
                            renderItem={renderRequirementItem}
                        />
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No must-match requirements"
                        />
                    )}
                </div>
            ),
        },
        {
            key: "other",
            label: (
                <Text style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }} type="warning">Other Requirements
                    ({otherRequirements.length})
                </Text>
            ),
            children: (
                <div className="requirements-tab-content">
                    {isRefreshing ? (
                        // Show skeleton loaders during refresh
                        <>
                            <RequirementItemSkeleton />
                            <RequirementItemSkeleton />
                        </>
                    ) : otherRequirements.length > 0 ? (
                        <List
                            size="small"
                            dataSource={otherRequirements}
                            renderItem={renderRequirementItem}
                        />
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No other requirements"
                        />
                    )}
                </div>
            ),
        },
    ];

    // Expanded content (detailed view) - Using Tabs
    const expandedContent = isInitialLoad ? null : (
        <div className="requirements-details">
            <Tabs
                defaultActiveKey="must-match"
                items={tabItems}
                size="small"
                className="requirements-tabs"
            />
        </div>
    );

    return (
        <CollapsibleInsightCard
            title="Profile Screening"
            subtitle={subtitle}
            icon={<UserOutlined />}
            onRefresh={handleRefresh}
            isRefreshing={loading}
            themeColor={themeColor}
            expandedContent={expandedContent}
            expanded={expanded}
            onExpandChange={onExpandChange}
        >
            {summaryContent}
        </CollapsibleInsightCard>
    );
};

export default RequirementsInsightCard;
